import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Alert,
} from "@mui/material";
import Card from "@/components/Card";
import { ProjectRecord } from "@/types";
import { getVirtualPeakerLogsReq, VirtualPeakerLog } from "@/api/adminApi";
import dayjs from "dayjs";
import SVGSelectArrowDown from "@/assets/icons/SVGSelectArrowDown";
import SVGSelectArrowUp from "@/assets/icons/SVGSelectArrowUp";

const OPERATION_LOGS_LABEL = "Operation Logs";

interface VirtualPeakerSectionProps {
  project: ProjectRecord;
}

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    const upperStatus = status?.toUpperCase() || "";
    if (upperStatus === "SUCCESS" || upperStatus === "ONLINE" || upperStatus === "ACTIVE") {
      return { bg: "#E8F5E9", color: "#2E7D32" };
    }
    if (upperStatus === "FAILED" || upperStatus === "ERROR" || upperStatus === "OFFLINE") {
      return { bg: "#FFEBEE", color: "#C62828" };
    }
    if (upperStatus === "STARTED" || upperStatus === "INFO" || upperStatus === "ENROLLED") {
      return { bg: "#E3F2FD", color: "#1565C0" };
    }
    if (upperStatus === "DEVICE_NOT_ATTACHED" || upperStatus === "NOT_ATTACHED") {
      return { bg: "#FFF3E0", color: "#E65100" };
    }
    return { bg: "#F5F5F5", color: "#616161" };
  };

  const colors = getStatusColor(status);

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        fontWeight: 600,
        fontSize: "12px",
      }}
    />
  );
};

const InfoItem: React.FC<{ label: string; value: string | number | null | undefined }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
    <Typography
      variant="body2"
      sx={{ color: "textLightGray3", fontWeight: 500, minWidth: "120px" }}
    >
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value || "Not set"}
    </Typography>
  </Box>
);

const VirtualPeakerSection: React.FC<VirtualPeakerSectionProps> = ({ project }) => {
  const [logs, setLogs] = useState<VirtualPeakerLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  
  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    const fetchLogs = async () => {
      if (!project?.id) return;
      
      setLoading(true);
      setError(null);
      try {
        const response = await getVirtualPeakerLogsReq(project.id);
        setLogs(response.data.logs || []);
      } catch (err: unknown) {
        console.error("Failed to fetch VP logs:", err);
        setError("Failed to load Virtual Peaker logs");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [project?.id]);

  const hasVpData = project?.billkey || project?.deviceId;

  if (!hasVpData && (loading || logs.length === 0)) {
    return null;
  }

  return (
    <Card
      sx={{
        padding: "24px",
        marginTop: "24px",
        borderRadius: "12px",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Virtual Peaker Integration
        </Typography>
        {project?.vpDeviceStatus && (
          <StatusChip status={project.vpDeviceStatus} />
        )}
      </Box>

      {project?.billkey && project?.deviceId == null && (
        <Alert severity="warning" sx={{ marginBottom: "16px" }}>
          Device pre-enrolled but not attached — enrollment is incomplete. Device ID was not returned by Virtual Peaker. Check {OPERATION_LOGS_LABEL} for details and contact support to retry.
        </Alert>
      )}

      {/* VP Info Fields */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: "16px",
          padding: "16px",
          backgroundColor: "#F8F9FA",
          borderRadius: "8px",
          marginBottom: "16px",
        }}
      >
        <InfoItem label="Bill Key" value={project?.billkey} />
        <InfoItem label="Device ID" value={project?.deviceId} />
        <InfoItem label="Device Status" value={project?.vpDeviceStatus} />
        <InfoItem
          label="Last Status Check"
          value={
            project?.vpDeviceStatusCheckedAt
              ? dayjs(project.vpDeviceStatusCheckedAt).format("MMM D, YYYY h:mm A")
              : null
          }
        />
      </Box>

      {/* VP Logs Section */}
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            padding: "8px 0",
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {OPERATION_LOGS_LABEL} ({logs.length})
          </Typography>
          <IconButton size="small">
            {expanded ? <SVGSelectArrowUp /> : <SVGSelectArrowDown />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ padding: "16px" }}>
              {error}
            </Typography>
          ) : logs.length === 0 ? (
            <Typography sx={{ padding: "16px", color: "textLightGray3" }}>
              No Virtual Peaker operations logged for this project.
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400, boxShadow: "none", border: "1px solid #E0E0E0" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: "#F5F5F5", width: 40 }} />
                    <TableCell sx={{ fontWeight: 600, backgroundColor: "#F5F5F5" }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: "#F5F5F5" }}>Session</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: "#F5F5F5" }}>Operation</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: "#F5F5F5" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: "#F5F5F5" }}>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => {
                    const isOpen = expandedRows.has(log.id);
                    const hasDetails =
                      log.data != null &&
                      (typeof log.data !== "object" || Object.keys(log.data).length > 0);
                    return (
                      <React.Fragment key={log.id}>
                        <TableRow hover>
                          <TableCell sx={{ width: 40, padding: "4px" }}>
                            {hasDetails && (
                              <IconButton
                                size="small"
                                aria-label={isOpen ? "Hide details" : "Show details"}
                                onClick={() => toggleRow(log.id)}
                              >
                                {isOpen ? <SVGSelectArrowUp /> : <SVGSelectArrowDown />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {dayjs(log.timestamp).format("MMM D, YYYY h:mm:ss A")}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "11px", color: "#757575" }}>
                            {typeof log.data === "object" && log.data?.sessionId != null
                              ? String(log.data.sessionId)
                              : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.operation}
                              size="small"
                              sx={{
                                backgroundColor: "#E3F2FD",
                                color: "#1565C0",
                                fontWeight: 500,
                                fontSize: "11px",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <StatusChip status={log.status} />
                          </TableCell>
                          {/* Long upstream messages now wrap (no ellipsis) so the
                              full error is visible per SDCP-1274. */}
                          <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {log.message}
                          </TableCell>
                        </TableRow>
                        {hasDetails && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ padding: 0, borderBottom: 0 }}>
                              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <Box
                                  sx={{
                                    margin: "8px 16px 16px 56px",
                                    padding: "12px",
                                    backgroundColor: "#FAFAFA",
                                    border: "1px solid #E0E0E0",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#757575", fontWeight: 600, display: "block", marginBottom: "4px" }}
                                  >
                                    Details
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      margin: 0,
                                      fontFamily: "monospace",
                                      fontSize: "11px",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                      color: "#212121",
                                    }}
                                  >
                                    {typeof log.data === "string"
                                      ? log.data
                                      : JSON.stringify(log.data, null, 2)}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Box>
    </Card>
  );
};

export default VirtualPeakerSection;
